import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../../../components/ui/dialog';
import { Input } from '../../../../../components/ui/input';
import { Label } from '../../../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../../components/ui/select';
import { Switch } from '../../../../../components/ui/switch';
import { Textarea } from '../../../../../components/ui/textarea';
import type { StatusType, WorkflowStatus, WorkflowType } from '@leal-crm/shared';

interface StatusEditDialogProps {
  status: WorkflowStatus | null;
  workflowType: WorkflowType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (updates: Partial<WorkflowStatus>) => void;
  isSaving: boolean;
}

export function StatusEditDialog({ status, workflowType, open, onOpenChange, onSave, isSaving }: StatusEditDialogProps) {
  const normalizeHexColor = (value: string | null | undefined, fallback: string) => {
    if (!value) return fallback;
    const normalized = value.trim();
    return /^#[0-9a-fA-F]{6}$/.test(normalized) ? normalized : fallback;
  };

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    customerLabel: '',
    statusType: 'regular' as StatusType,
    isInitial: false,
    isVisibleToCustomer: true,
    showInJobboard: true,
    color: '#6B7280',
    bgColor: '#F3F4F6',
    borderColor: '#E5E7EB',
  });

  const [syncedStatusId, setSyncedStatusId] = useState<string | null>(null);

  if (status && syncedStatusId !== status.id) {
    setFormData({
      name: status.name,
      description: status.description || '',
      customerLabel: status.customerLabel || '',
      statusType: status.statusType || 'regular',
      isInitial: status.isInitial,
      isVisibleToCustomer: status.isVisibleToCustomer,
      showInJobboard: status.showInJobboard,
      color: normalizeHexColor(status.color, '#6B7280'),
      bgColor: normalizeHexColor(status.bgColor, '#F3F4F6'),
      borderColor: normalizeHexColor(status.borderColor, '#E5E7EB'),
    });
    setSyncedStatusId(status.id);
  }

  if (!open && syncedStatusId !== null) {
    setSyncedStatusId(null);
  }

  if (!status) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar estado</DialogTitle>
          <DialogDescription>{status.name}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="customerLabel">Etiqueta cliente</Label>
              <Input
                id="customerLabel"
                value={formData.customerLabel}
                onChange={(e) => setFormData((f) => ({ ...f, customerLabel: e.target.value }))}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descripción</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData((f) => ({ ...f, description: e.target.value }))}
              rows={2}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Tipo de estado</Label>
              <Select
                value={formData.statusType}
                onValueChange={(value) => setFormData((f) => ({ ...f, statusType: value as StatusType }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="start">Inicio</SelectItem>
                  <SelectItem value="regular">Regular</SelectItem>
                  <SelectItem value="terminal">Terminal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Inicial</Label>
              <div className="flex items-center justify-between rounded-lg border px-3 py-2">
                <span className="text-sm text-muted-foreground">Estado inicial del flujo</span>
                <Switch
                  checked={formData.isInitial}
                  onCheckedChange={(checked) => setFormData((f) => ({ ...f, isInitial: checked }))}
                />
              </div>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label>Color texto</Label>
              <Input
                type="color"
                value={normalizeHexColor(formData.color, '#6B7280')}
                onChange={(e) => setFormData((f) => ({ ...f, color: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Color fondo</Label>
              <Input
                type="color"
                value={normalizeHexColor(formData.bgColor, '#F3F4F6')}
                onChange={(e) => setFormData((f) => ({ ...f, bgColor: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Color borde</Label>
              <Input
                type="color"
                value={normalizeHexColor(formData.borderColor, '#E5E7EB')}
                onChange={(e) => setFormData((f) => ({ ...f, borderColor: e.target.value }))}
              />
            </div>
          </div>

          <div className="space-y-3 border-t pt-4">
            {workflowType === 'booking' ? (
              <div className="flex items-center justify-between">
                <div>
                  <Label>Visible para cliente</Label>
                  <p className="text-xs text-muted-foreground">El cliente puede ver este estado</p>
                </div>
                <Switch
                  checked={formData.isVisibleToCustomer}
                  onCheckedChange={(checked) => setFormData((f) => ({ ...f, isVisibleToCustomer: checked }))}
                />
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  <Label>Mostrar en tablero</Label>
                  <p className="text-xs text-muted-foreground">Aparece como columna en el tablero</p>
                </div>
                <Switch
                  checked={formData.showInJobboard}
                  onCheckedChange={(checked) => setFormData((f) => ({ ...f, showInJobboard: checked }))}
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancelar
          </Button>
          <Button
            onClick={() =>
              onSave({
                name: formData.name.trim(),
                description: formData.description.trim() || undefined,
                customerLabel: formData.customerLabel.trim() || undefined,
                statusType: formData.statusType,
                isInitial: formData.isInitial,
                isVisibleToCustomer: workflowType === 'booking' ? formData.isVisibleToCustomer : undefined,
                showInJobboard: workflowType === 'job' ? formData.showInJobboard : undefined,
                color: normalizeHexColor(formData.color, '#6B7280'),
                bgColor: normalizeHexColor(formData.bgColor, '#F3F4F6'),
                borderColor: normalizeHexColor(formData.borderColor, '#E5E7EB'),
              })
            }
            disabled={isSaving}
          >
            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Guardar cambios
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
